import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, AlertCircle, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { fmtDateTime, relativeTime } from '../../../utils/format';
import { actionLabel, entityLink, fieldDiffLines, isFailedAction } from './activityMeta';

// "Field: old → new" lines for a mutation row (doc-142). Collapsed behind a
// native <details> so a 40-field save can't blow up the row height, but the
// summary still says how many fields moved without a click.
function FieldDiff({ metadata }) {
  const lines = fieldDiffLines(metadata);
  if (lines.length === 0) return null;
  const omitted = Number(metadata?.omittedFields) || 0;
  return (
    <details style={{ marginTop: 4 }}>
      <summary style={{ fontSize: 11.5, color: 'var(--text-tertiary)', cursor: 'pointer' }}>
        {lines.length} field{lines.length === 1 ? '' : 's'} changed
      </summary>
      <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 460 }}>
        {lines.map((line) => (
          <div key={line.field} style={{ fontSize: 11.5, lineHeight: 1.5, overflowWrap: 'anywhere' }}>
            <span style={{ color: 'var(--text-secondary)' }}>{line.label}: </span>
            <span style={{ color: 'var(--text-tertiary)', textDecoration: 'line-through' }}>
              {line.from === null ? '—' : line.from}
            </span>
            <span style={{ color: 'var(--text-tertiary)' }}> → </span>
            <span style={{ color: 'var(--text-primary)' }}>{line.to === null ? '—' : line.to}</span>
          </div>
        ))}
        {omitted > 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
            +{omitted} more field{omitted === 1 ? '' : 's'} not recorded
          </div>
        )}
      </div>
    </details>
  );
}

export default function ActivityTable({
  entries,
  loading,
  error,
  page,
  pageSize,
  pageSizeOptions,
  pagination,
  setPage,
  handlePageSizeChange,
  hasFilters,
  clearFilters,
}) {
  if (error) {
    return (
      <div className="callout red">
        <AlertCircle className="w-4 h-4" style={{ color: 'var(--color-red-500)', flexShrink: 0, marginTop: 1 }} />
        <div className="callout-body" style={{ fontSize: 12.5 }}>{error}</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--icon-secondary)' }} />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="card">
        <div className="card-body" style={{ padding: 48, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
          <Activity className="w-5 h-5" style={{ margin: '0 auto 8px', color: 'var(--icon-tertiary)' }} />
          {hasFilters ? (
            <>
              No activity matches these filters.
              <div style={{ marginTop: 10 }}>
                <button className="btn outline xs" onClick={clearFilters}>Clear filters</button>
              </div>
            </>
          ) : 'No activity recorded yet.'}
        </div>
      </div>
    );
  }

  const totalPages = pagination.totalPages || 1;

  return (
    <div className="card">
      <table className="tbl">
        <thead>
          <tr>
            <th style={{ paddingLeft: 24, width: 150 }}>When</th>
            <th style={{ width: 220 }}>User</th>
            <th>Action</th>
            <th style={{ paddingRight: 24 }}>Target</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => {
            const link = entityLink(e.entityType, e.entityId);
            return (
              <tr key={e.id} style={{ cursor: 'default' }}>
                <td style={{ paddingLeft: 24, fontSize: 12, color: 'var(--text-tertiary)' }} title={fmtDateTime(e.createdAt)}>
                  {relativeTime(e.createdAt)}
                </td>
                <td style={{ fontSize: 12.5 }}>
                  {/* API-key actions have no user row; the audit middleware stores the
                      key's display name in metadata, so show that instead of a dash.
                      No user AND no key means a background job (expiry sweep, startup
                      tasks) wrote the entry — label it System rather than a dash. */}
                  <div style={{ color: 'var(--text-primary)' }}>
                    {e.userName || e.userEmail || e.metadata?.serviceKeyName || 'System'}
                  </div>
                  {e.userRole ? (
                    <div className="muted" style={{ fontSize: 11 }}>{e.userRole}</div>
                  ) : (!e.userName && !e.userEmail ? (
                    <div className="muted" style={{ fontSize: 11 }}>
                      {e.metadata?.serviceKeyName ? 'API key' : 'automated'}
                    </div>
                  ) : null)}
                </td>
                <td style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                  <span className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {actionLabel(e.action)}
                    {isFailedAction(e.action) && (
                      <span className="chip red sq" title={e.statusCode ? `HTTP ${e.statusCode}` : 'Background task failed'}>
                        {e.statusCode || 'Failed'}
                      </span>
                    )}
                    {e.metadata?.source === 'slack' && <span className="chip sky sq" title="Originated in Slack">Slack</span>}
                  </span>
                  {/* The error message the user saw — the whole point of
                      recording failures (doc: admin error visibility). */}
                  {isFailedAction(e.action) && e.metadata?.error && (
                    <div style={{ marginTop: 3, fontSize: 11.5, color: 'var(--text-critical)', maxWidth: 460, overflowWrap: 'anywhere' }}>
                      {e.metadata.error}
                      {e.metadata.filename ? <span style={{ color: 'var(--text-tertiary)' }}> · {e.metadata.filename}</span> : null}
                    </div>
                  )}
                  <FieldDiff metadata={e.metadata} />
                </td>
                <td style={{ paddingRight: 24, fontSize: 12.5 }}>
                  {e.entityType ? (
                    link ? (
                      <Link to={link} className="mono" style={{ fontSize: 11 }}>
                        {e.entityType} · {String(e.entityId).slice(0, 8)}
                      </Link>
                    ) : (
                      <span className="muted mono" style={{ fontSize: 11 }}>
                        {/* Role/grant rows carry the target's email — far more
                            readable than a uuid slice for an unlinkable user. */}
                        {e.entityType}{e.metadata?.targetEmail
                          ? ` · ${e.metadata.targetEmail}`
                          : (e.entityId ? ` · ${String(e.entityId).slice(0, 8)}` : '')}
                      </span>
                    )
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div
        style={{
          padding: '10px 18px',
          borderTop: '0.5px solid var(--border-primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: 12,
          color: 'var(--text-secondary)',
        }}
      >
        <div className="row" style={{ gap: 12 }}>
          <span>
            Showing <strong style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
              {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, pagination.total)}
            </strong> of {pagination.total}
          </span>
          <span className="row" style={{ gap: 6 }}>
            <span>Per page:</span>
            <select
              value={pageSize}
              onChange={(event) => handlePageSizeChange(parseInt(event.target.value, 10))}
              className="filter-select"
              style={{ height: 26, fontSize: 12 }}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </span>
        </div>
        <div className="row" style={{ gap: 4 }}>
          <button
            className="icon-btn xs"
            onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
            disabled={page === 1}
            aria-label="Previous page"
            style={{ opacity: page === 1 ? 0.4 : 1 }}
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <span className="mono" style={{ padding: '0 8px' }}>
            {page} / {totalPages}
          </span>
          <button
            className="icon-btn xs"
            onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
            disabled={page >= totalPages}
            aria-label="Next page"
            style={{ opacity: page >= totalPages ? 0.4 : 1 }}
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
